import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import Login from './components/Login';
import AccessGate from './components/AccessGate';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Settings from './pages/Settings';
import AccessManagement from './pages/AccessManagement';
import PublicDownload from './pages/PublicDownload';
import { LoadingState } from './components/ui';

/** Only invited, verified people (and admins) get past this. */
const ProtectedRoute: React.FC<{ children: React.ReactNode; adminOnly?: boolean }> = ({ children, adminOnly }) => {
  const { status, isAdmin } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <LoadingState />;
  if (status === 'signedOut') return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (status !== 'ready') return <AccessGate />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
};

const LoginRoute: React.FC = () => {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <LoadingState />;
  if (status === 'signedOut') return <Login />;
  if (status !== 'ready') return <AccessGate />;
  const from = (location.state as { from?: string } | null)?.from;
  return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
};

const ThemedToaster: React.FC = () => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  return (
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 4000,
        style: {
          background: dark ? '#222b40' : '#ffffff',
          color: dark ? '#ebeff6' : '#161e30',
          border: `1px solid ${dark ? '#343e55' : '#d9dfea'}`,
          fontSize: '14px',
          boxShadow: '0 8px 24px -12px rgba(13, 19, 34, 0.35)',
        },
        success: { duration: 3000, iconTheme: { primary: '#16b3d9', secondary: '#fff' } },
        error: { duration: 6000, iconTheme: { primary: '#dc2626', secondary: '#fff' } },
      }}
    />
  );
};

function App() {
  return (
    <Router>
      <LanguageProvider>
        <ThemeProvider>
          <AuthProvider>
            <ThemedToaster />
            <Routes>
              <Route path="/login" element={<LoginRoute />} />
              <Route path="/share/:hash" element={<PublicDownload />} />
              <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
              <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/access" element={<ProtectedRoute adminOnly><AccessManagement /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </ThemeProvider>
      </LanguageProvider>
    </Router>
  );
}

export default App;
